import React, { useState, useContext, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoaderContext } from '../../App';
import AICopilotConfigService from '../../core/service/AICopilotConfigService';
import './GeneratePromptModal.scss';

const GeneratePromptModal = ({ t, show, onHide, selectedFramework, selectedCategory, selectedTopic, onTransferPrompts }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [generatedPrompts, setGeneratedPrompts] = useState({
    functional_prompt: '',
    technical_prompt1: '',
    technical_prompt2: ''
  });
  // Add state for checkboxes
  const [selectedPrompts, setSelectedPrompts] = useState({
    functional_prompt: false,
    technical_prompt1: false,
    technical_prompt2: false
  });

  const { setLoaderDisplay } = useContext(LoaderContext);

  // Clear modal data when modal is opened
  useEffect(() => {
    if (show) {
      setSelectedFile(null);
      setGeneratedPrompts({
        functional_prompt: '',
        technical_prompt1: '',
        technical_prompt2: ''
      });
      setSelectedPrompts({
        functional_prompt: false,
        technical_prompt1: false,
        technical_prompt2: false
      });
    }
  }, [show]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.warning(t("GeneratePromptModal.validation.onlyPdfAllowed.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      event.target.value = '';
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleGenerate = async () => {
    if (!selectedFile) {
      toast.warning(t("GeneratePromptModal.validation.selectFile.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      return;
    }

    if (!selectedFramework || !selectedCategory || !selectedTopic) {
      toast.error(t("GeneratePromptModal.validation.selectFrameworkCategoryTopic.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      return;
    }

    setLoaderDisplay(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', selectedCategory);
      formData.append('topic', selectedTopic);
      formData.append('language', 'en-us');
      formData.append('framework', selectedFramework);

      const response = await AICopilotConfigService.generatePrompt(formData);
      
      if (response && response.data) {
        setGeneratedPrompts({
          functional_prompt: response.data.functional_prompt || '',
          technical_prompt1: response.data.technical_prompt1 || '',
          technical_prompt2: response.data.technical_prompt2 || ''
        });
        toast.success(t("GeneratePromptModal.generateSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
      } else {
        toast.error(t("GeneratePromptModal.generateError.label"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Error generating prompts:', error);
      toast.error(t("GeneratePromptModal.generateError.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
    } finally {
      setLoaderDisplay(false);
    }
  };

  const handleTransfer = () => {
    // Only transfer selected prompts
    const promptsToTransfer = {};
    let anySelected = false;
    Object.keys(selectedPrompts).forEach(key => {
      if (selectedPrompts[key] && generatedPrompts[key]) {
        promptsToTransfer[key] = generatedPrompts[key];
        anySelected = true;
      }
    });
    if (anySelected) {
      onTransferPrompts(promptsToTransfer);
      // Clear modal data after successful transfer
      setSelectedFile(null);
      setGeneratedPrompts({
        functional_prompt: '',
        technical_prompt1: '',
        technical_prompt2: ''
      });
      setSelectedPrompts({
        functional_prompt: false,
        technical_prompt1: false,
        technical_prompt2: false
      });
      onHide();
    } else {
      toast.warning(t("GeneratePromptModal.validation.noPromptSelected.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
    }
  };

  const handleClose = () => {
    // Clear all modal data when closing
    setSelectedFile(null);
    setGeneratedPrompts({
      functional_prompt: '',
      technical_prompt1: '',
      technical_prompt2: ''
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("GeneratePromptModal.title.label")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("GeneratePromptModal.framework.label")}</Form.Label>
              <Form.Control
                type="text"
                value={selectedFramework || ''}
                readOnly
                className="bg-light"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("GeneratePromptModal.category.label")}</Form.Label>
              <Form.Control
                type="text"
                value={(selectedCategory || '').toUpperCase()}
                readOnly
                className="bg-light"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("GeneratePromptModal.topic.label")}</Form.Label>
              <Form.Control
                type="text"
                value={selectedTopic || ''}
                readOnly
                className="bg-light"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>{t("GeneratePromptModal.fileUpload.label")}</Form.Label>
          <Form.Control
            type="file"
            onChange={handleFileChange}
            accept=".pdf"
          />
          <Form.Text className="text-muted">
            {t("GeneratePromptModal.fileUpload.help.label")}
          </Form.Text>
        </Form.Group>

        <div className="text-center">
          <Button 
            variant="primary" 
            onClick={handleGenerate}
            disabled={!selectedFile}
            size="sm"
          >
            {t("GeneratePromptModal.generate.label")}
          </Button>
        </div>

        {(generatedPrompts.functional_prompt || generatedPrompts.technical_prompt1 || generatedPrompts.technical_prompt2) && (
          <div className="mt-4">
            <h6>{t("GeneratePromptModal.generatedPrompts.label")}</h6>
            {generatedPrompts.functional_prompt && (
              <Form.Group className="mb-3">
                <div className="prompt-checkbox-label-row">
                  <Form.Check
                    type="checkbox"
                    id="checkbox-functional-prompt"
                    className="me-2"
                    checked={selectedPrompts.functional_prompt}
                    onChange={e => setSelectedPrompts(sp => ({ ...sp, functional_prompt: e.target.checked }))}
                    disabled={!generatedPrompts.functional_prompt}
                    data-testid="checkbox-functional-prompt"
                  />
                  <Form.Label htmlFor="checkbox-functional-prompt" className="mb-0">
                    {t("GeneratePromptModal.functionalPrompt.label")}
                  </Form.Label>
                </div>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={generatedPrompts.functional_prompt}
                  readOnly
                  className="bg-light"
                />
              </Form.Group>
            )}

            {generatedPrompts.technical_prompt1 && (
              <Form.Group className="mb-3">
                <div className="prompt-checkbox-label-row">
                  <Form.Check
                    type="checkbox"
                    id="checkbox-technical-prompt1"
                    className="me-2"
                    checked={selectedPrompts.technical_prompt1}
                    onChange={e => setSelectedPrompts(sp => ({ ...sp, technical_prompt1: e.target.checked }))}
                    disabled={!generatedPrompts.technical_prompt1}
                    data-testid="checkbox-technical-prompt1"
                  />
                  <Form.Label htmlFor="checkbox-technical-prompt1" className="mb-0">
                    {t("GeneratePromptModal.technicalPrompt1.label")}
                  </Form.Label>
                </div>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={generatedPrompts.technical_prompt1}
                  readOnly
                  className="bg-light"
                />
              </Form.Group>
            )}

            {generatedPrompts.technical_prompt2 && (
              <Form.Group className="mb-3">
                <div className="prompt-checkbox-label-row">
                  <Form.Check
                    type="checkbox"
                    id="checkbox-technical-prompt2"
                    className="me-2"
                    checked={selectedPrompts.technical_prompt2}
                    onChange={e => setSelectedPrompts(sp => ({ ...sp, technical_prompt2: e.target.checked }))}
                    disabled={!generatedPrompts.technical_prompt2}
                    data-testid="checkbox-technical-prompt2"
                  />
                  <Form.Label htmlFor="checkbox-technical-prompt2" className="mb-0">
                    {t("GeneratePromptModal.technicalPrompt2.label")}
                  </Form.Label>
                </div>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={generatedPrompts.technical_prompt2}
                  readOnly
                  className="bg-light"
                />
              </Form.Group>
            )}
            <div className="d-grid gap-2">
              <Button 
                variant="success" 
                onClick={handleTransfer}
              >
                {t("GeneratePromptModal.transfer.label")}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default withTranslation()(GeneratePromptModal); 